import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { useAdminEdgeFunction } from "@/hooks/useAdminEdgeFunction";

interface CreateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const CreateTemplateDialog = ({
  open,
  onOpenChange,
  onSuccess,
}: CreateTemplateDialogProps) => {
  const { invokeAdmin, isLoading } = useAdminEdgeFunction();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    function_name: "",
    base_prompt: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { error } = await invokeAdmin("create-prompt-template", {
      body: formData,
      showErrorToast: true,
    });

    if (!error) {
      onSuccess();
      setFormData({ name: "", description: "", function_name: "", base_prompt: "" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Prompt Template</DialogTitle>
          <DialogDescription>
            Create a new prompt template for use across edge functions
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Template Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Chat Response Template"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this template is used for..."
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="function_name">Edge Function Name</Label>
            <Input
              id="function_name"
              value={formData.function_name}
              onChange={(e) => setFormData({ ...formData, function_name: e.target.value })}
              placeholder="e.g., chat"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="base_prompt">Base Prompt</Label>
            <Textarea
              id="base_prompt"
              value={formData.base_prompt}
              onChange={(e) => setFormData({ ...formData, base_prompt: e.target.value })}
              placeholder="Enter the base prompt text..."
              className="min-h-[200px] font-mono text-sm"
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Creating..." : "Create Template"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
